import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('erp_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('erp_token');
      localStorage.removeItem('erp_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (data) => api.post('/auth/register', data),
  joinCompany: (data, companyCode) => api.post(`/auth/join-company?company_code=${companyCode}`, data),
  getMe: () => api.get('/auth/me'),
};

// Company API
export const companyAPI = {
  get: () => api.get('/company'),
  update: (data) => api.put('/company', data),
  getWooSettings: () => api.get('/company/woocommerce'),
  updateWooSettings: (data) => api.put('/company/woocommerce', data),
  getStorefrontSettings: () => api.get('/company/storefront'),
  updateStorefrontSettings: (data) => api.put('/company/storefront', data),
  updateLogo: (logo) => api.put('/company/logo', { logo }),
  removeLogo: () => api.delete('/company/logo'),
};

export const publicAPI = {
  getStorefrontStatus: () => api.get('/public/storefront-status'),
  createAdminPreview: () => api.post('/storefront/admin-preview-token'),
};

// Users API
export const usersAPI = {
  getAll: () => api.get('/users'),
  getPending: () => api.get('/users/pending'),
  create: (data) => api.post('/users', data),
  updateRole: (userId, role) => api.put(`/users/${userId}/role?role=${role}`),
  updateStatus: (userId, status) => api.put(`/users/${userId}/status?status=${status}`),
  approve: (userId) => api.put(`/users/${userId}/approve`),
  reject: (userId) => api.put(`/users/${userId}/reject`),
  delete: (userId) => api.delete(`/users/${userId}`),
};

// Products API
export const productsAPI = {
  getAll: (params) => api.get('/products', { params }),
  getOne: (id) => api.get(`/products/${id}`),
  create: (data) => api.post('/products', data),
  update: (id, data) => api.put(`/products/${id}`, data),
  delete: (id) => api.delete(`/products/${id}`),
  getCategories: () => api.get('/products/categories/list'),
  getVariations: (id) => api.get(`/variations/product/${id}`),
};

export const sizeGuidesAPI = {
  getAll: (includeInactive = false) => api.get('/size-guides', { params: { include_inactive: includeInactive } }),
  getOne: (id) => api.get(`/size-guides/${id}`),
  create: (data) => api.post('/size-guides', data),
  update: (id, data) => api.put(`/size-guides/${id}`, data),
  delete: (id) => api.delete(`/size-guides/${id}`),
};

export const collectionsAPI = {
  getAll: () => api.get('/catalog/collections'),
  getOne: (id) => api.get(`/catalog/collections/${id}`),
  create: (data) => api.post('/catalog/collections', data),
  update: (id, data) => api.put(`/catalog/collections/${id}`, data),
  delete: (id) => api.delete(`/catalog/collections/${id}`),
};

export const couponsAPI = {
  getAll: (includeArchived = false) => api.get('/coupons', { params: { include_archived: includeArchived } }),
  getSummary: () => api.get('/coupons/summary'),
  getOne: (id) => api.get(`/coupons/${id}`),
  create: (data) => api.post('/coupons', data),
  update: (id, data) => api.put(`/coupons/${id}`, data),
  updateStatus: (id, isActive) => api.patch(`/coupons/${id}/status`, { is_active: isActive }),
  delete: (id) => api.delete(`/coupons/${id}`),
};

// Cloudinary media API. Files upload directly from the ERP browser to
// Cloudinary using a short-lived signature issued by the authenticated backend.
export const mediaAPI = {
  getConfig: () => api.get('/media/config'),
  listLibrary: ({ nextCursor = '', maxResults = 60 } = {}) => api.get('/media/library', {
    params: {
      max_results: maxResults,
      ...(nextCursor ? { next_cursor: nextCursor } : {}),
    },
  }),
  createUploadSignature: () => api.post('/media/upload-signature'),
  importUrl: (url, alt = '') => api.post('/media/import-url', { url, alt }),
  deleteAsset: (publicId) => api.delete('/media/asset', { data: { public_id: publicId } }),
  uploadFile: async (file, onProgress) => {
    const signatureResponse = await api.post('/media/upload-signature');
    const config = signatureResponse.data;
    const extension = (file.name.split('.').pop() || '').toLowerCase();

    if (!file.type.startsWith('image/')) {
      throw new Error(`${file.name} is not an image`);
    }
    if (!config.allowed_formats.includes(extension)) {
      throw new Error(`${file.name}: use JPG, JPEG, PNG, WebP, or AVIF`);
    }
    if (file.size > config.max_file_size) {
      throw new Error(`${file.name} is larger than 10 MB`);
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('api_key', config.api_key);
    formData.append('timestamp', String(config.timestamp));
    formData.append('signature', config.signature);
    formData.append('folder', config.folder);
    if (config.upload_preset) formData.append('upload_preset', config.upload_preset);

    const response = await axios.post(config.upload_url, formData, {
      onUploadProgress: (event) => {
        if (onProgress && event.total) onProgress(Math.round((event.loaded * 100) / event.total));
      },
    });
    const result = response.data;
    return {
      url: result.secure_url,
      public_id: result.public_id,
      alt: '',
      width: result.width || 0,
      height: result.height || 0,
      format: result.format || extension,
      bytes: result.bytes || file.size,
      version: result.version || 0,
    };
  },
};

// Inventory API
export const inventoryAPI = {
  getMovements: (productId) => api.get('/inventory/movements', { params: { product_id: productId } }),
  createMovement: (data) => api.post('/inventory/movements', data),
  getLowStock: () => api.get('/inventory/low-stock'),
  getValuation: () => api.get('/inventory/valuation'),
};

// Customers API
export const customersAPI = {
  getAll: (search) => api.get('/customers', { params: { search } }),
  getOne: (id) => api.get(`/customers/${id}`),
  create: (data) => api.post('/customers', data),
  update: (id, data) => api.put(`/customers/${id}`, data),
  delete: (id) => api.delete(`/customers/${id}`),
};

// Suppliers API
export const suppliersAPI = {
  getAll: (search) => api.get('/suppliers', { params: { search } }),
  getOne: (id) => api.get(`/suppliers/${id}`),
  create: (data) => api.post('/suppliers', data),
  update: (id, data) => api.put(`/suppliers/${id}`, data),
  delete: (id) => api.delete(`/suppliers/${id}`),
};

// Sales Orders API
export const salesOrdersAPI = {
  getAll: (params) => api.get('/sales-orders', { params }),
  getOne: (id) => api.get(`/sales-orders/${id}`),
  create: (data) => api.post('/sales-orders', data),
  quote: (data) => api.post('/sales-orders/quote', data),
  update: (id, data) => api.put(`/sales-orders/${id}`, data),
  cancel: (id) => api.post(`/sales-orders/${id}/cancel`),
  return: (id, confirmInventory = false) => api.post(`/sales-orders/${id}/return`, null, { params: { confirm_inventory: confirmInventory } }),
  completeRefund: (id, data) => api.post(`/sales-orders/${id}/complete-refund`, data),
};

// Courier COD settlement workflow
export const codSettlementsAPI = {
  getAll: (params) => api.get('/cod-settlements', { params }),
  create: (orderId, data) => api.post(`/cod-settlements/${orderId}`, data),
};

// Invoices API
export const invoicesAPI = {
  getAll: (status) => api.get('/invoices', { params: { status } }),
  getOne: (id) => api.get(`/invoices/${id}`),
  getPdf: (id) => api.get(`/invoices/${id}/pdf`, { responseType: 'blob' }),
};

// Purchase Orders API
export const purchaseOrdersAPI = {
  getAll: (params) => api.get('/purchase-orders', { params }),
  getOne: (id) => api.get(`/purchase-orders/${id}`),
  create: (data) => api.post('/purchase-orders', data),
  update: (id, data) => api.put(`/purchase-orders/${id}`, data),
  delete: (id) => api.delete(`/purchase-orders/${id}`),
  // Direct PO receipt is intentionally disabled; use the GRN workflow.
  receive: (id) => Promise.reject(new Error(`Purchase Order ${id} must be received through GRN`)),
};

// Payments API
export const paymentsAPI = {
  getAll: (params) => api.get('/payments', { params }),
  create: (data) => api.post('/payments', data),
  getSummary: () => api.get('/payments/summary'),
};

// Accounting API
export const accountingAPI = {
  getEntries: (params) => api.get('/accounting/entries', { params }),
  createEntry: (data) => api.post('/accounting/entries', data),
  getProfitLoss: (params) => api.get('/accounting/profit-loss', { params }),
  getReceivables: () => api.get('/accounting/receivables'),
  getPayables: () => api.get('/accounting/payables'),
  getCancelledReceivableCandidates: () => api.get('/finance/receivables/cancelled-candidates'),
  clearCancelledReceivables: (data) => api.post('/finance/receivables/clear-cancelled', data),
};


// Enterprise Finance Assistant API
export const financeAssistantAPI = {
  suggest: (data) => api.post('/finance-assistant/suggest', data),
  saveDecision: (suggestionId, data) => api.post(`/finance-assistant/suggestions/${suggestionId}/decision`, data),
  getTaxonomy: () => api.get('/finance-assistant/taxonomy'),
  getSettings: () => api.get('/finance-assistant/settings'),
  updateSettings: (data) => api.put('/finance-assistant/settings', data),
  getHealth: () => api.get('/finance-assistant/health'),
  getPostingRules: () => api.get('/finance-assistant/posting-rules'),
  createPostingRule: (data) => api.post('/finance-assistant/posting-rules', data),
  updatePostingRule: (id, data) => api.put(`/finance-assistant/posting-rules/${id}`, data),
  getReviewQueue: (params) => api.get('/finance-assistant/review-queue', { params }),
  resolveReview: (id, data) => api.post(`/finance-assistant/review-queue/${id}/resolve`, data),
  getDecisionAnalytics: () => api.get('/finance-assistant/decision-analytics'),
};

// Dashboard API
export const dashboardAPI = {
  getSummary: (params) => api.get('/dashboard/summary', { params }),
  getSalesChart: (params) => api.get('/dashboard/sales-chart', { params }),
  getTopProducts: (params) => api.get('/dashboard/top-products', { params }),
};

// Notifications API
export const notificationsAPI = {
  getAll: (params) => api.get('/notifications', { params }),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/mark-all-read'),
  delete: (id) => api.delete(`/notifications/${id}`),
  clearAll: () => api.delete('/notifications'),
  // Preferences
  getPreferences: () => api.get('/notifications/preferences'),
  updatePreferences: (data) => api.put('/notifications/preferences', data),
  // Email providers (Admin). Secrets stay in DigitalOcean environment variables.
  getEmailProviderStatus: () => api.get('/notifications/email-provider-status'),
  testTransactionalEmail: (email) => api.post(`/notifications/email-providers/test-transactional?test_email=${encodeURIComponent(email)}`),
  getEmailOutbox: (params) => api.get('/notifications/email-outbox', { params }),
  // Send notification (Admin)
  send: (data) => api.post('/notifications/send', data),
  getPushPublicKey: () => api.get('/notifications/push/public-key'),
  subscribePush: (data) => api.post('/notifications/push/subscribe', data),
  unsubscribePush: (endpoint) => api.delete('/notifications/push/subscribe', { params: { endpoint } }),
};

// Audit Logs API
export const auditLogsAPI = {
  getAll: (limit) => api.get('/audit-logs', { params: { limit } }),
};

// Reports API
export const reportsAPI = {
  getSales: (params) => api.get('/reports/sales', { params }),
};

// RM Procurement API
export const rmProcurementAPI = {
  // RM Suppliers
  getSuppliers: (search) => api.get('/rm-procurement/suppliers', { params: { search } }),
  getSupplier: (id) => api.get(`/rm-procurement/suppliers/${id}`),
  createSupplier: (data) => api.post('/rm-procurement/suppliers', data),
  updateSupplier: (id, data) => api.put(`/rm-procurement/suppliers/${id}`, data),
  deleteSupplier: (id) => api.delete(`/rm-procurement/suppliers/${id}`),
  
  // RM Purchase Orders
  getPurchaseOrders: (params) => api.get('/rm-procurement/purchase-orders', { params }),
  getPurchaseOrder: (id) => api.get(`/rm-procurement/purchase-orders/${id}`),
  createPurchaseOrder: (data) => api.post('/rm-procurement/purchase-orders', data),
  updatePurchaseOrder: (id, data) => api.put(`/rm-procurement/purchase-orders/${id}`, data),
  approvePurchaseOrder: (id) => api.post(`/rm-procurement/purchase-orders/${id}/approve`),
  deletePurchaseOrder: (id) => api.delete(`/rm-procurement/purchase-orders/${id}`),
  recordPayment: (id, data) => api.post(`/rm-procurement/purchase-orders/${id}/record-payment`, null, { params: data }),
  
  // RM GRN
  getGRNs: (params) => api.get('/rm-procurement/grn', { params }),
  getGRN: (id) => api.get(`/rm-procurement/grn/${id}`),
  createGRN: (data) => api.post('/rm-procurement/grn', data),
  
  // RM GRN Returns
  getGRNReturns: (params) => api.get('/rm-procurement/grn-returns', { params }),
  createGRNReturn: (data) => api.post('/rm-procurement/grn-returns', data),
  
  // Accounts Payable
  getAccountsPayable: (params) => api.get('/rm-procurement/accounts-payable', { params }),
};

// Payroll API
export const payrollAPI = {
  // Departments
  getDepartments: () => api.get('/payroll/departments'),
  createDepartment: (data) => api.post('/payroll/departments', data),
  updateDepartment: (id, data) => api.put(`/payroll/departments/${id}`, data),
  deleteDepartment: (id) => api.delete(`/payroll/departments/${id}`),
  
  // Designations
  getDesignations: (params) => api.get('/payroll/designations', { params }),
  createDesignation: (data) => api.post('/payroll/designations', data),
  updateDesignation: (id, data) => api.put(`/payroll/designations/${id}`, data),
  deleteDesignation: (id) => api.delete(`/payroll/designations/${id}`),
  
  // Seed Organization Structure
  seedOrgStructure: () => api.post('/payroll/seed-org-structure'),
  
  // Employees
  getEmployees: (params) => api.get('/payroll/employees', { params }),
  getEmployee: (id) => api.get(`/payroll/employees/${id}`),
  getNextEmployeeId: () => api.get('/payroll/employees/next-id/generate'),
  createEmployee: (data) => api.post('/payroll/employees', data),
  updateEmployee: (id, data) => api.put(`/payroll/employees/${id}`, data),
  terminateEmployee: (id) => api.delete(`/payroll/employees/${id}`),
  
  // Salary Structure
  getSalaryStructure: () => api.get('/payroll/salary-structure'),
  updateSalaryStructure: (data) => api.put('/payroll/salary-structure', data),
  addAllowance: (data) => api.post('/payroll/salary-structure/allowances', data),
  deleteAllowance: (id) => api.delete(`/payroll/salary-structure/allowances/${id}`),
  
  // Leave Management
  getLeaveBalances: (params) => api.get('/payroll/leave/balances', { params }),
  updateLeaveBalance: (employeeId, data) => api.put(`/payroll/leave/balances/${employeeId}`, data),
  getLeaveRequests: (params) => api.get('/payroll/leave/requests', { params }),
  createLeaveRequest: (data) => api.post('/payroll/leave/requests', data),
  approveLeave: (id) => api.post(`/payroll/leave/requests/${id}/approve`),
  rejectLeave: (id, reason) => api.post(`/payroll/leave/requests/${id}/reject`, null, { params: { reason } }),
  
  // Advances & Loans
  getAdvances: (params) => api.get('/payroll/advances', { params }),
  createAdvance: (data) => api.post('/payroll/advances', data),
  
  // Payroll Processing
  getPayrolls: (params) => api.get('/payroll/payrolls', { params }),
  getPayroll: (id) => api.get(`/payroll/payrolls/${id}`),
  createPayroll: (data) => api.post('/payroll/payrolls', data),
  updatePayrollItem: (payrollId, itemId, data) => api.put(`/payroll/payrolls/${payrollId}/items/${itemId}`, data),
  submitPayroll: (id) => api.post(`/payroll/payrolls/${id}/submit`),
  approvePayroll: (id) => api.post(`/payroll/payrolls/${id}/approve`),
  processPayroll: (id, bankAccountId) => api.post(`/payroll/payrolls/${id}/process`, null, { params: { bank_account_id: bankAccountId } }),
  deletePayroll: (id) => api.delete(`/payroll/payrolls/${id}`),
  
  // Task Payments
  getTaskPayments: (params) => api.get('/payroll/task-payments', { params }),
  createTaskPayment: (data) => api.post('/payroll/task-payments', data),
  
  // Task Assignments
  getTasks: (params) => api.get('/payroll/tasks', { params }),
  getTask: (id) => api.get(`/payroll/tasks/${id}`),
  getTaskCategories: () => api.get('/payroll/tasks/categories'),
  getPendingTaskPayments: () => api.get('/payroll/tasks/pending-payment'),
  createTask: (data) => api.post('/payroll/tasks', data),
  updateTask: (id, data) => api.put(`/payroll/tasks/${id}`, data),
  startTask: (id) => api.post(`/payroll/tasks/${id}/start`),
  completeTask: (id, notes) => api.post(`/payroll/tasks/${id}/complete`, null, { params: { notes } }),
  verifyTask: (id) => api.post(`/payroll/tasks/${id}/verify`),
  rejectTask: (id, reason) => api.post(`/payroll/tasks/${id}/reject`, null, { params: { reason } }),
  cancelTask: (id, reason) => api.post(`/payroll/tasks/${id}/cancel`, null, { params: { reason } }),
  getEmployeeTaskSummary: (employeeId) => api.get(`/payroll/tasks/employee/${employeeId}/summary`),
  
  // Attendance Tracking
  getAttendance: (params) => api.get('/payroll/attendance', { params }),
  getDailyAttendance: (date) => api.get(`/payroll/attendance/daily/${date}`),
  createAttendance: (data) => api.post('/payroll/attendance', data),
  createBulkAttendance: (data) => api.post('/payroll/attendance/bulk', data),
  updateAttendance: (id, data) => api.put(`/payroll/attendance/${id}`, data),
  deleteAttendance: (id) => api.delete(`/payroll/attendance/${id}`),
  getEmployeeAttendanceSummary: (employeeId, month) => api.get(`/payroll/attendance/summary/${employeeId}`, { params: { month } }),
  getMonthlyAttendanceReport: (params) => api.get('/payroll/attendance/monthly-report', { params }),
  getAttendanceSettings: () => api.get('/payroll/attendance/settings'),
  
  // Reports
  getPayslip: (payrollId, employeeId) => api.get(`/payroll/reports/payslip/${payrollId}/${employeeId}`),
  getPayrollSummary: (params) => api.get('/payroll/reports/summary', { params }),
  getEpfEtfReport: (params) => api.get('/payroll/reports/epf-etf', { params }),
  getDepartmentReport: (params) => api.get('/payroll/reports/department', { params }),
  
  // Payslip PDF Download
  downloadPayslipPdf: (payrollId, employeeId) => {
    const token = localStorage.getItem('erp_token');
    const baseUrl = process.env.REACT_APP_BACKEND_URL;
    window.open(`${baseUrl}/api/payroll/payslips/${payrollId}/${employeeId}/pdf?token=${token}`, '_blank');
  },
  
  // My Payslips (Employee Self-Service)
  getMyPayslips: () => api.get('/payroll/my-payslips'),
};

// Seed Demo Data
export const seedDemoData = () => api.post('/seed-demo-data');

// PayHere Settlements API
export const payhereAPI = {
  getPending: () => api.get('/finance/payhere/pending'),
  getBankAccounts: () => api.get('/bank-accounts'),
  createSettlement: (data) => api.post('/finance/payhere/settlements', data),
  listSettlements: () => api.get('/finance/payhere/settlements'),
  getSettlement: (id) => api.get(`/finance/payhere/settlements/${id}`),
  getMigrationCandidates: () => api.get('/finance/payhere/migration/candidates'),
  migrate: (data) => api.post('/finance/payhere/migration/migrate', data),
};

// Admin API
export const adminAPI = {
  // System Info
  getSystemInfo: () => api.get('/admin/system-info'),
  
  // Backups
  getBackups: () => api.get('/admin/backups'),
  getBackup: (id) => api.get(`/admin/backups/${id}`),
  createBackup: (data) => api.post('/admin/backups', data),
  deleteBackup: (id) => api.delete(`/admin/backups/${id}`),
  uploadBackup: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/admin/backups/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  
  // Restore
  getRestorePreview: (backupId) => api.get(`/admin/restore/preview/${backupId}`),
  restore: (data) => api.post('/admin/restore', data),
  
  // Data Reset
  getResetPreview: (resetType) => api.get('/admin/data-reset/preview', { params: { reset_type: resetType } }),
  resetData: (data) => api.post('/admin/data-reset', data),
};

export default api;

// Fixed Asset Management API — deliberately separate from product PO/GRN/inventory.
export const fixedAssetsAPI = {
  setup: {
    status: () => api.get('/fixed-assets/setup/status'),
    accountSuggestions: () => api.get('/fixed-assets/setup/account-suggestions'),
    initialize: (data) => api.post('/fixed-assets/setup/initialize', data),
    settings: () => api.get('/fixed-assets/settings'),
    updateSettings: (data) => api.put('/fixed-assets/settings', data),
    periods: () => api.get('/finance/financial-periods'),
    createPeriod: (data) => api.post('/finance/financial-periods', data),
    closePeriod: (id) => api.post(`/finance/financial-periods/${id}/close`),
    reopenPeriod: (id) => api.post(`/finance/financial-periods/${id}/reopen`),
  },
  dashboard: () => api.get('/fixed-assets/dashboard'),
  assets: {
    list: (params) => api.get('/fixed-assets', { params }),
    create: (data) => api.post('/fixed-assets', data),
    get: (id) => api.get(`/fixed-assets/${id}`),
    update: (id, data) => api.patch(`/fixed-assets/${id}`, data),
    submit: (id) => api.post(`/fixed-assets/${id}/submit`),
    approve: (id) => api.post(`/fixed-assets/${id}/approve`),
    reject: (id, comments) => api.post(`/fixed-assets/${id}/reject`, { comments }),
    activate: (id) => api.post(`/fixed-assets/${id}/activate`),
    usefulLife: (id, data) => api.post(`/fixed-assets/${id}/useful-life`, data),
    qr: (id) => api.get(`/fixed-assets/${id}/qr`, { responseType: 'blob' }),
  },
  categories: {
    list: () => api.get('/fixed-assets/categories'),
    get: (id) => api.get(`/fixed-assets/categories/${id}`),
    create: (data) => api.post('/fixed-assets/categories', data),
    update: (id, data) => api.put(`/fixed-assets/categories/${id}`, data),
    remove: (id) => api.delete(`/fixed-assets/categories/${id}`),
  },
  groups: {
    list: () => api.get('/fixed-assets/groups'),
    create: (data) => api.post('/fixed-assets/groups', data),
    update: (id, data) => api.put(`/fixed-assets/groups/${id}`, data),
  },
  locations: {
    list: () => api.get('/fixed-assets/locations'),
    create: (data) => api.post('/fixed-assets/locations', data),
    update: (id, data) => api.put(`/fixed-assets/locations/${id}`, data),
    remove: (id) => api.delete(`/fixed-assets/locations/${id}`),
  },
  acquisitions: {
    referenceData: () => api.get('/fixed-assets/acquisition-reference-data'),
    requests: (params) => api.get('/fixed-assets/acquisition-requests', { params }),
    createRequest: (data) => api.post('/fixed-assets/acquisition-requests', data),
    submitRequest: (id) => api.post(`/fixed-assets/acquisition-requests/${id}/submit`),
    approveRequest: (id, comments = null) => api.post(`/fixed-assets/acquisition-requests/${id}/approve`, { comments }),
    purchaseOrders: (params) => api.get('/fixed-assets/purchase-orders', { params }),
    createPurchaseOrder: (data) => api.post('/fixed-assets/purchase-orders', data),
    approvePurchaseOrder: (id, comments = null) => api.post(`/fixed-assets/purchase-orders/${id}/approve`, { comments }),
    cancelPurchaseOrder: (id, comments = null) => api.post(`/fixed-assets/purchase-orders/${id}/cancel`, { comments }),
    receipts: (params) => api.get('/fixed-assets/receipts', { params }),
    createReceipt: (data) => api.post('/fixed-assets/receipts', data),
    invoices: (params) => api.get('/fixed-assets/supplier-invoices', { params }),
    createInvoice: (data) => api.post('/fixed-assets/supplier-invoices', data),
    approveInvoice: (id, comments = null) => api.post(`/fixed-assets/supplier-invoices/${id}/approve`, { comments }),
    postInvoice: (id) => api.post(`/fixed-assets/supplier-invoices/${id}/post`),
    reverseInvoice: (id, data) => api.post(`/fixed-assets/supplier-invoices/${id}/reverse`, data),
  },
  payments: {
    list: (params) => api.get('/fixed-assets/payments', { params }),
    create: (data) => api.post('/fixed-assets/payments', data),
    reverse: (id, data) => api.post(`/fixed-assets/payments/${id}/reverse`, data),
  },
  capitalizations: {
    list: (params) => api.get('/fixed-assets/capitalizations', { params }),
    create: (data) => api.post('/fixed-assets/capitalizations', data),
    submit: (id) => api.post(`/fixed-assets/capitalizations/${id}/submit`),
    approve: (id, comments = null) => api.post(`/fixed-assets/capitalizations/${id}/approve`, { comments }),
    post: (id) => api.post(`/fixed-assets/capitalizations/${id}/post`),
    reverse: (id, data) => api.post(`/fixed-assets/capitalizations/${id}/reverse`, data),
  },
  depreciation: {
    schedule: (assetId) => api.get(`/fixed-assets/${assetId}/depreciation-schedule`),
    runs: (params) => api.get('/fixed-assets/depreciation-runs', { params }),
    createRun: (data) => api.post('/fixed-assets/depreciation-runs', data),
    calculate: (id) => api.post(`/fixed-assets/depreciation-runs/${id}/calculate`),
    validate: (id) => api.post(`/fixed-assets/depreciation-runs/${id}/validate`),
    submit: (id) => api.post(`/fixed-assets/depreciation-runs/${id}/submit`),
    approve: (id) => api.post(`/fixed-assets/depreciation-runs/${id}/approve`),
    post: (id) => api.post(`/fixed-assets/depreciation-runs/${id}/post`),
    reverse: (id, data) => api.post(`/fixed-assets/depreciation-runs/${id}/reverse`, data),
  },
  cwip: {
    list: () => api.get('/fixed-assets/cwip'),
    create: (data) => api.post('/fixed-assets/cwip', data),
    addCost: (id, data) => api.post(`/fixed-assets/cwip/${id}/costs`, data),
    capitalize: (id, data) => api.post(`/fixed-assets/cwip/${id}/capitalize`, data),
  },
  assignments: {
    list: (params) => api.get('/fixed-assets/assignments', { params }),
    assign: (assetId, data) => api.post(`/fixed-assets/${assetId}/assign`, data),
    acknowledge: (id, comments) => api.post(`/fixed-assets/assignments/${id}/acknowledge`, { comments }),
    requestReturn: (id, comments) => api.post(`/fixed-assets/assignments/${id}/request-return`, { comments }),
    return: (id, data) => api.post(`/fixed-assets/assignments/${id}/return`, data),
  },
  transfers: {
    list: (params) => api.get('/fixed-assets/transfers', { params }),
    create: (assetId, data) => api.post(`/fixed-assets/${assetId}/transfer`, data),
    approve: (id, comments) => api.post(`/fixed-assets/transfers/${id}/approve`, { comments }),
    release: (id, comments) => api.post(`/fixed-assets/transfers/${id}/release`, { comments }),
    receive: (id, data) => api.post(`/fixed-assets/transfers/${id}/receive`, data),
  },
  maintenance: {
    list: (params) => api.get('/fixed-assets/maintenance', { params }),
    create: (assetId, data) => api.post(`/fixed-assets/${assetId}/maintenance`, data),
    approve: (id, comments) => api.post(`/fixed-assets/maintenance/${id}/approve`, { comments }),
    complete: (id, data) => api.post(`/fixed-assets/maintenance/${id}/complete`, data),
  },
  improvements: {
    list: (params) => api.get('/fixed-assets/improvements', { params }),
    create: (assetId, data) => api.post(`/fixed-assets/${assetId}/improvements`, data),
  },
  impairments: {
    list: (params) => api.get('/fixed-assets/impairments', { params }),
    create: (assetId, data) => api.post(`/fixed-assets/${assetId}/impairments`, data),
    reverse: (id, data) => api.post(`/fixed-assets/impairments/${id}/reverse`, data),
  },
  revaluations: {
    list: (params) => api.get('/fixed-assets/revaluations', { params }),
    create: (assetId, data) => api.post(`/fixed-assets/${assetId}/revaluations`, data),
  },
  disposals: {
    list: (params) => api.get('/fixed-assets/disposals', { params }),
    create: (assetId, data) => api.post(`/fixed-assets/${assetId}/disposals`, data),
    reverse: (id, data) => api.post(`/fixed-assets/disposals/${id}/reverse`, data),
    saleInvoices: () => api.get('/fixed-assets/sale-invoices'),
    createSaleInvoice: (data) => api.post('/fixed-assets/sale-invoices', data),
  },
  incidents: {
    list: (params) => api.get('/fixed-assets/incidents', { params }),
    create: (assetId, data) => api.post(`/fixed-assets/${assetId}/incidents`, data),
    resolve: (id, data) => api.post(`/fixed-assets/incidents/${id}/resolve`, data),
  },
  stocktakes: {
    list: (params) => api.get('/fixed-assets/stocktakes', { params }),
    create: (data) => api.post('/fixed-assets/stocktakes', data),
    start: (id) => api.post(`/fixed-assets/stocktakes/${id}/start`),
    verify: (id, data) => api.post(`/fixed-assets/stocktakes/${id}/verify`, data),
    complete: (id) => api.post(`/fixed-assets/stocktakes/${id}/complete`),
    lines: (id) => api.get(`/fixed-assets/stocktakes/${id}/lines`),
  },
  documents: {
    list: (params) => api.get('/fixed-assets/documents', { params }),
    create: (data) => api.post('/fixed-assets/documents', data),
    remove: (id) => api.delete(`/fixed-assets/documents/${id}`),
  },
  approvals: {
    list: (params) => api.get('/fixed-assets/approvals', { params }),
    act: (id, decision, comments) => api.post(`/fixed-assets/approvals/${id}/${decision}`, { comments }),
  },
  openingBalances: {
    preview: (data) => api.post('/fixed-assets/opening-balances/preview', data),
    commit: (data) => api.post('/fixed-assets/opening-balances/commit', data),
  },
  imports: {
    upload: (entityType, file) => {
      const data = new FormData();
      data.append('file', file);
      return api.post(`/fixed-assets/imports/${entityType}`, data, { headers: { 'Content-Type': 'multipart/form-data' } });
    },
  },
  reports: {
    download: (report, params, output = 'csv') => api.get(`/fixed-assets/reports/${report}`, { params: { ...params, output }, responseType: 'blob' }),
    register: (params) => api.get('/fixed-assets/reports/register', { params }),
    additions: (params) => api.get('/fixed-assets/reports/additions', { params }),
    depreciation: (params) => api.get('/fixed-assets/reports/depreciation', { params }),
    disposals: (params) => api.get('/fixed-assets/reports/disposals', { params }),
    maintenance: (params) => api.get('/fixed-assets/reports/maintenance', { params }),
    stocktake: (params) => api.get('/fixed-assets/reports/stocktake', { params }),
    reconciliation: () => api.get('/fixed-assets/reports/reconciliation'),
    rollForward: (params) => api.get('/fixed-assets/reports/roll-forward', { params }),
    operational: (reportType) => api.get('/fixed-assets/reports/operational', { params: { report_type: reportType } }),
  },
  jobs: { runReminders: () => api.post('/fixed-assets/jobs/reminders/run') },
};
